import { Component, Prop, h, State, Element } from '@stencil/core';
@Component({
    tag: 'amwal-installments-guide',
    styleUrl: 'amwal-installments-guide.css',
    shadow: false,
})
export class AmwalInstallmentsGuide {
    @Element() el: HTMLElement;

    private selectRef: HTMLSelectElement;

    /**
     * Language locale: 'en' for English, 'ar' for Arabic
     */
    @Prop() locale: 'en' | 'ar' = 'en';

    @State() currentLang: 'en' | 'ar' = this.locale;

    componentWillLoad() {
        this.currentLang = this.locale;
        this.applyLanguage(this.currentLang);
        if (this.selectRef) {
            this.selectRef.value = this.currentLang;
        }
    }

    componentDidUpdate() {
        // Keep select in sync with state
        if (this.selectRef) {
            this.selectRef.value = this.currentLang;
        }
    }


    private applyLanguage(lang: 'en' | 'ar') {
        this.currentLang = lang;
        if (typeof document !== 'undefined') {
            document.documentElement.lang = lang;
            document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
        }
    }
    private handleLanguageChange = (event: Event) => {
        const select = event.target as HTMLSelectElement;
        const newLang = select.value as 'en' | 'ar';
        this.applyLanguage(newLang);
    }

    private renderEnglishContent() {
        return (
                <div class="lang-en">
                    <header class="hero">
                        <div class="container hero-inner">
                            <div>
                                <span class="badge" aria-label="Guide badge">End‑User Guide</span>
                                <h1 class="title">Amwal Bank Installments</h1>
                                <p class="subtitle">Split eligible purchases into easy monthly payments with your own bank—no upfront
                                    down
                                    payment.</p>
                                <div class="hero-card">
                                    <div class="kpis">
                                        <div class="kpi">
                                            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <path d="M20 6 9 17l-5-5" stroke="#128c4b" stroke-width="2" stroke-linecap="round"
                                                    stroke-linejoin="round"/>
                                            </svg>
                                            <div><strong>95% approval rate</strong><br/><span class="muted">Fast decisions for eligible
                                    cards</span></div>
                                        </div>
                                        <div class="kpi">
                                            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <circle cx="12" cy="12" r="9" stroke="#128c4b" stroke-width="2"/>
                                                <path d="M12 7v5l4 2" stroke="#128c4b" stroke-width="2" stroke-linecap="round"/>
                                            </svg>
                                            <div><strong>3, 6, 12‑month plans</strong><br/><span
                                                    class="muted">Depending on your bank</span></div>
                                        </div>
                                        <div class="kpi">
                                            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <path d="M4 7h16M4 12h16M4 17h10" stroke="#128c4b" stroke-width="2"
                                                    stroke-linecap="round"/>
                                                <rect x="3" y="5" width="18" height="14" rx="3" stroke="#128c4b" stroke-width="2"/>
                                            </svg>
                                            <div><strong>Earn rewards & card points</strong><br/><span class="muted">Keep your card benefits</span>
                                            </div>
                                        </div>
                                        <div class="kpi">
                                            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <path d="M12 3v18M5 9h14M5 15h14" stroke="#128c4b" stroke-width="2"
                                                    stroke-linecap="round"/>
                                                <rect x="3" y="5" width="18" height="14" rx="3" stroke="#128c4b" stroke-width="2"/>
                                            </svg>
                                            <div><strong>Use up to 90% of card limit</strong><br/><span class="muted">Subject to bank policy</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div class="card">
                                    <h3>At a glance</h3>
                                    <ul class="list">
                                        <li><span class="dot"></span> No upfront down payment required</li>
                                        <li><span class="dot"></span> First installment typically due in <strong>30–55 days</strong>
                                        </li>
                                        <li><span class="dot"></span> Bank converts your purchase into installments within <strong>2–4
                                            days</strong></li>
                                    </ul>
                                    <p class="notice" role="note">Your full purchase amount must be available in your credit card limit
                                        at the
                                        time of checkout. Your available limit will be reduced by that amount immediately.</p>
                                </div>
                            </div>
                        </div>
                    </header>

                    <main class="container">
                        <section class="grid">
                            <div class="card">
                                <h2>How it works</h2>
                                <ol class="list steps">
                                    <li>Select <strong>Amwal Bank Installments</strong> at checkout.</li>
                                    <li>Choose your bank and the plan length (3, 6, or 12 months).</li>
                                    <li>Pay using the credit card associated with that bank, or Apple Pay (linked to that card).</li>
                                    <li>Your bank converts the purchase into monthly installments after approval.</li>
                                </ol>
                            </div>
                            <div class="card">
                                <h2>Monthly payments</h2>
                                <p>Installments are <strong>automatic</strong>: they're either debited from your bank balance or added
                                    to your
                                    card's monthly statement. No extra action is required from you.</p>
                                <p class="notice">Minimum purchase amounts for installments are set by each bank and merchant.</p>
                            </div>
                        </section>

                        <section class="grid">
                            <div class="card example">
                                <h2>Examples</h2>
                                <div class="pair">
                                    <h3>Al Rajhi Bank — Equal Monthly Installments</h3>
                                    <p>A 6,000 SAR purchase is divided into equal monthly payments across your selected plan.</p>
                                    <amwal-installments-timeline amount={6000} installments-count="12" enable-installments="true"  locale="en" timeline-style="default" footer-message=""></amwal-installments-timeline>
                                </div>
                                <div class="pair">
                                    <h3>Saudi National Bank — 5% Monthly Installments</h3>
                                    <p>Across a 12‑month plan, you pay <strong>5% of the total</strong> each cycle. At the end of the
                                        term, the
                                        remaining balance becomes due.</p>
                                    <amwal-installments-timeline amount={6000} installments-count="12" enable-installments="true"  locale="en" timeline-style="default" footer-message=""  installment-data='[{"amount":"300.00","dueDate":"2025-11-08","status":"upcoming","installmentNumber":1},{"amount":"300.00","dueDate":"2025-12-08","status":"upcoming","installmentNumber":2},{"amount":"300.00","dueDate":"2026-01-08","status":"upcoming","installmentNumber":3},{"amount":"300.00","dueDate":"2026-02-08","status":"upcoming","installmentNumber":4},{"amount":"300.00","dueDate":"2026-03-08","status":"upcoming","installmentNumber":5},{"amount":"300.00","dueDate":"2026-04-08","status":"upcoming","installmentNumber":6},{"amount":"300.00","dueDate":"2026-05-08","status":"upcoming","installmentNumber":7},{"amount":"300.00","dueDate":"2026-06-08","status":"upcoming","installmentNumber":8},{"amount":"300.00","dueDate":"2026-07-08","status":"upcoming","installmentNumber":9},{"amount":"300.00","dueDate":"2026-08-08","status":"upcoming","installmentNumber":10},{"amount":"300.00","dueDate":"2026-09-08","status":"upcoming","installmentNumber":11},{"amount":"300.00","dueDate":"2026-10-08","status":"upcoming","installmentNumber":12}]'></amwal-installments-timeline>
                                </div>
                            </div>
                        </section>

                        <section>
                            <h2>FAQs</h2>
                            <details>
                                <summary>
                                    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M8 5l8 7-8 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"
                                            stroke-linejoin="round"/>
                                    </svg>
                                    Do I earn card rewards and points?
                                </summary>
                                <div class="faq-body">
                                    <p>Yes. You continue to earn your credit card rewards and points on eligible installment purchases,
                                        as per
                                        your bank's policy.</p>
                                </div>
                            </details>
                            <details>
                                <summary>
                                    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M8 5l8 7-8 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"
                                            stroke-linejoin="round"/>
                                    </svg>
                                    When is my first payment due?
                                </summary>
                                <div class="faq-body">
                                    <p>Typically within <strong>30–55 days</strong> from the purchase date, depending on your bank's
                                        billing
                                        cycle.</p>
                                </div>
                            </details>
                            <details>
                                <summary>
                                    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M8 5l8 7-8 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"
                                            stroke-linejoin="round"/>
                                    </svg>
                                    Does my card limit change?
                                </summary>
                                <div class="faq-body">
                                    <p>Yes. Your available credit limit is reduced by the full purchase amount at checkout. After
                                        conversion to
                                        installments, it may replenish as you pay installments (subject to bank policy).</p>
                                </div>
                            </details>
                            <details>
                                <summary>
                                    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M8 5l8 7-8 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"
                                            stroke-linejoin="round"/>
                                    </svg>
                                    Which plans are available?
                                </summary>
                                <div class="faq-body">
                                    <p>Common options are <strong>3, 6, and 12 months</strong>. Availability depends on your issuing
                                        bank and
                                        the merchant.</p>
                                </div>
                            </details>
                            <details>
                                <summary>
                                    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M8 5l8 7-8 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"
                                            stroke-linejoin="round"/>
                                    </svg>
                                    How quickly does the bank convert my purchase?
                                </summary>
                                <div class="faq-body">
                                    <p>Usually within <strong>2–4 days</strong> after a successful checkout.</p>
                                </div>
                            </details>
                            <details>
                                <summary>
                                    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M8 5l8 7-8 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"
                                            stroke-linejoin="round"/>
                                    </svg>
                                    Why was my installment request rejected?
                                </summary>
                                <div class="faq-body">
                                    <ul class="list">
                                        <li><span class="dot"></span> Card isn't eligible for installments</li>
                                        <li><span class="dot"></span> More than four active installment plans on the same card</li>
                                        <li><span class="dot"></span> Using loaded/temporary balance to try to increase card limit</li>
                                        <li><span class="dot"></span> Requests are subject to the issuing bank's terms & conditions</li>
                                    </ul>
                                </div>
                            </details>
                        </section>

                        <footer>
                            <p>© <span id="year-en"></span> Amwal. Information provided for customer guidance; actual eligibility,
                                limits,
                                and plans are determined by your issuing bank and merchant.</p>
                        </footer>
                    </main>
                </div>
        );
    }

    private renderArabicContent() {
        return (
                    <div class="lang-ar">
                        <header class="hero">
                            <div class="container hero-inner">
                                <div>
                                    <span class="badge" aria-label="Guide badge">دليل المستخدم</span>
                                    <h1 class="title">أقساط البنوك من أمـوال</h1>
                                    <p class="subtitle">قسّم مشترياتك المؤهلة إلى دفعات شهرية ميسّرة مع بنكك — دون دفعة مقدّمة.</p>
                                    <div class="hero-card">
                                        <div class="kpis">
                                            <div class="kpi">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                    <path d="M20 6 9 17l-5-5" stroke="#128c4b" stroke-width="2" stroke-linecap="round"
                                                        stroke-linejoin="round"/>
                                                </svg>
                                                <div><strong>معدل موافقة يتجاوز ‎%95‎</strong><br/><span class="muted">قرارات سريعة للبطاقات
                                        المؤهلة</span></div>
                                            </div>
                                            <div class="kpi">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                    <circle cx="12" cy="12" r="9" stroke="#128c4b" stroke-width="2"/>
                                                    <path d="M12 7v5l4 2" stroke="#128c4b" stroke-width="2" stroke-linecap="round"/>
                                                </svg>
                                                <div><strong>خطط 3 أو 6 أو 12 شهراً</strong><br/><span class="muted">حسب بنكك</span></div>
                                            </div>
                                            <div class="kpi">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                    <path d="M4 7h16M4 12h16M4 17h10" stroke="#128c4b" stroke-width="2"
                                                        stroke-linecap="round"/>
                                                    <rect x="3" y="5" width="18" height="14" rx="3" stroke="#128c4b" stroke-width="2"/>
                                                </svg>
                                                <div><strong>اكسب المكافآت ونقاط البطاقة</strong><br/><span class="muted">احتفظ بمزايا بطاقتك</span>
                                                </div>
                                            </div>
                                            <div class="kpi">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                    <path d="M12 3v18M5 9h14M5 15h14" stroke="#128c4b" stroke-width="2"
                                                        stroke-linecap="round"/>
                                                    <rect x="3" y="5" width="18" height="14" rx="3" stroke="#128c4b" stroke-width="2"/>
                                                </svg>
                                                <div><strong>حتى ‎90%‎ من حد البطاقة</strong><br/><span
                                                        class="muted">وفقاً لسياسة البنك</span></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div class="card">
                                        <h3>نظرة سريعة</h3>
                                        <ul class="list">
                                            <li><span class="dot"></span> لا توجد دفعة مقدّمة</li>
                                            <li><span class="dot"></span> أول دفعة عادةً خلال <strong>30–55 يوماً</strong></li>
                                            <li><span class="dot"></span> يحوّل البنك العملية إلى أقساط خلال <strong>2–4 أيام</strong></li>
                                        </ul>
                                        <p class="notice" role="note">يجب أن يكون كامل مبلغ الشراء متاحاً ضمن حد بطاقتك الائتمانية عند
                                            الدفع. سيتم
                                            حجز المبلغ كاملاً من حدك المتاح فوراً.</p>
                                    </div>
                                </div>
                            </div>
                        </header>

                        <main class="container">
                            <section class="grid">
                                <div class="card">
                                    <h2>كيف تعمل؟</h2>
                                    <ol class="list steps">
                                        <li>اختر <strong>أقساط أمـوال البنكية</strong> عند الدفع.</li>
                                        <li>اختر بنكك ومدة الخطة (3 أو 6 أو 12 شهراً).</li>
                                        <li>ادفع بالبطاقة المرتبطة بذلك البنك، أو عبر Apple Pay المرتبط بالبطاقة نفسها.</li>
                                        <li>بعد الموافقة، يحوّل البنك الشراء إلى أقساط شهرية.</li>
                                    </ol>
                                </div>
                                <div class="card">
                                    <h2>الدفعات الشهرية</h2>
                                    <p>تُخصم الدفعات <strong>تلقائياً</strong> من رصيدك البنكي أو تُضاف إلى كشف بطاقتك الشهري. لا يلزمك أي
                                        إجراء
                                        إضافي.</p>
                                    <p class="notice">الحد الأدنى لقيمة الشراء للتقسيط يحدده كل بنك والتاجر.</p>
                                </div>
                            </section>

                            <section class="grid">
                                <div class="card example">
                                    <h2>أمثلة</h2>
                                    <div class="pair">
                                        <h3>مصرف الراجحي — أقساط شهرية متساوية</h3>
                                        <p>يُقسَّم شراء بقيمة 6,000 ريال سعودي إلى دفعات شهرية متساوية حسب الخطة التي تختارها.</p>
                                        <amwal-installments-timeline amount={6000} installments-count="12" enable-installments="true"  locale="ar" timeline-style="default" footer-message=""></amwal-installments-timeline>
                                    </div>
                                    <div class="pair">
                                        <h3>البنك الأهلي السعودي — أقساط ‎5%‎ شهرياً</h3>
                                        <p>ضمن خطة 12 شهراً، تدفع <strong>5% من الإجمالي</strong> في كل دورة. وفي نهاية المدة يُسدد الرصيد
                                            المتبقي.
                                        </p>
                                        <amwal-installments-timeline amount={6000} installments-count="12" enable-installments="true"  locale="ar" timeline-style="default" footer-message=""  installment-data='[{"amount":"300.00","dueDate":"2025-11-08","status":"upcoming","installmentNumber":1},{"amount":"300.00","dueDate":"2025-12-08","status":"upcoming","installmentNumber":2},{"amount":"300.00","dueDate":"2026-01-08","status":"upcoming","installmentNumber":3},{"amount":"300.00","dueDate":"2026-02-08","status":"upcoming","installmentNumber":4},{"amount":"300.00","dueDate":"2026-03-08","status":"upcoming","installmentNumber":5},{"amount":"300.00","dueDate":"2026-04-08","status":"upcoming","installmentNumber":6},{"amount":"300.00","dueDate":"2026-05-08","status":"upcoming","installmentNumber":7},{"amount":"300.00","dueDate":"2026-06-08","status":"upcoming","installmentNumber":8},{"amount":"300.00","dueDate":"2026-07-08","status":"upcoming","installmentNumber":9},{"amount":"300.00","dueDate":"2026-08-08","status":"upcoming","installmentNumber":10},{"amount":"300.00","dueDate":"2026-09-08","status":"upcoming","installmentNumber":11},{"amount":"300.00","dueDate":"2026-10-08","status":"upcoming","installmentNumber":12}]'></amwal-installments-timeline>
                                    </div>
                                </div>
                            </section>

                            <section class="FAQ">
                                <h2>الأسئلة الشائعة</h2>
                                <details>
                                    <summary>
                                        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                            <path d="M8 5l8 7-8 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"
                                                stroke-linejoin="round"/>
                                        </svg>
                                        هل أكسب مكافآت ونقاط البطاقة؟
                                    </summary>
                                    <div class="faq-body">
                                        <p>نعم. تستمر في كسب مكافآت البطاقة ونقاطها على المشتريات المؤهلة للتقسيط وفق سياسة بنكك.</p>
                                    </div>
                                </details>
                                <details>
                                    <summary>
                                        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                            <path d="M8 5l8 7-8 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"
                                                stroke-linejoin="round"/>
                                        </svg>
                                        متى موعد أول دفعة؟
                                    </summary>
                                    <div class="faq-body">
                                        <p>عادةً خلال <strong>30–55 يوماً</strong> من تاريخ الشراء، بحسب دورة الفوترة في بنكك.</p>
                                    </div>
                                </details>
                                <details>
                                    <summary>
                                        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                            <path d="M8 5l8 7-8 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"
                                                stroke-linejoin="round"/>
                                        </svg>
                                        هل يتغير حد بطاقتي؟
                                    </summary>
                                    <div class="faq-body">
                                        <p>نعم. يُحجز كامل مبلغ الشراء من حد بطاقتك المتاح عند الدفع. وبعد التحويل إلى أقساط قد يعود الحد
                                            للتزايد مع
                                            سدادك للأقساط (وفق سياسة البنك).</p>
                                    </div>
                                </details>
                                <details>
                                    <summary>
                                        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                            <path d="M8 5l8 7-8 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"
                                                stroke-linejoin="round"/>
                                        </svg>
                                        ما الخطط المتاحة؟
                                    </summary>
                                    <div class="faq-body">
                                        <p>الخيارات الشائعة هي <strong>3 أو 6 أو 12 شهراً</strong>. وتختلف الإتاحة حسب البنك المُصدر
                                            والتاجر.</p>
                                    </div>
                                </details>
                                <details>
                                    <summary>
                                        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                            <path d="M8 5l8 7-8 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"
                                                stroke-linejoin="round"/>
                                        </svg>
                                        كم يستغرق تحويل الشراء إلى أقساط؟
                                    </summary>
                                    <div class="faq-body">
                                        <p>عادةً خلال <strong>2–4 أيام</strong> بعد إتمام الشراء بنجاح.</p>
                                    </div>
                                </details>
                                <details>
                                    <summary>
                                        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                            <path d="M8 5l8 7-8 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"
                                                stroke-linejoin="round"/>
                                        </svg>
                                        لماذا رُفض طلب التقسيط؟
                                    </summary>
                                    <div class="faq-body">
                                        <ul class="list">
                                            <li><span class="dot"></span> البطاقة غير مؤهلة للتقسيط</li>
                                            <li><span class="dot"></span> وجود أكثر من أربع خطط تقسيط نشطة على البطاقة نفسها</li>
                                            <li><span class="dot"></span> استخدام رصيد مُحمّل/مؤقت لمحاولة زيادة الحد</li>
                                            <li><span class="dot"></span> الطلبات خاضعة لشروط وأحكام البنك المُصدر</li>
                                        </ul>
                                    </div>
                                </details>
                            </section>

                            <footer>
                                <p>© <span id="year-ar"></span> أمـوال. المعلومات لغرض الإرشاد؛ الأهلية والحدود والخطط يحدّدها البنك المُصدر
                                    والتاجر.</p>
                            </footer>
                        </main>
                    </div>

        );
    }

    render() {
        return (
            <div class="amwal-installments-guide">
                <div class="topbar">
                    <div class="topbar-inner">
                        <a class="brand" href="https://amwal.tech" target="_blank" rel="noopener noreferrer">
                            <img class="brand-logo" src="https://cdn.prod.website-files.com/62294ce746440b7bc08b4fc5/624352eb48193d537d329386_1-2.png" alt="Amwal logo"/>
                        </a>
                        <div class="lang-controls">
                            <label class="lang-en">Language:</label>
                            <label class="lang-ar">اللغة:</label>
                            <select
                                class="lang-select"
                                aria-label="Language selector"
                                ref={(el) => this.selectRef = el}
                                onChange={this.handleLanguageChange}
                            >
                                <option value="en" selected>English</option>
                                <option value="ar">العربية</option>
                            </select>
                        </div>
                    </div>
                </div>
                {this.currentLang === 'en' ? this.renderEnglishContent() : this.renderArabicContent()}
            </div>
        );
    }
}